import React, { useEffect, useState, useRef } from 'react';

import { meta } from '~/commons/config';
import useHttpStatus from '~/commons/hooks/useHttpStatus';
import PageHttp from '~/commons/components/PageHttp.jsx';
import Html from '../../commons/components/Html.jsx';

const PageMock = () => {
  const [resComp, reqComp] = useHttpStatus();
  const [resMarkings, reqMarkings] = useHttpStatus();
  const [userAnswers, setUserAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const scoreRef = useRef(null);

  meta.set({ title: '模擬挑戰' });

  const loadNewQuestions = () => {
    setUserAnswers({});
    setShowResults(false);
    reqComp({
      url: 'api/quiz/competitions/mock',
    });
  };

  const getQuestionResult = (questionId) => {
    if (!resMarkings.payload || !resMarkings.payload.marked) return null;
    return resMarkings.payload.marked.find(
      (result) => result.questionId.toString() === questionId.toString(),
    );
  };

  const getChoiceStyle = (questionId, choiceIndex, choiceText) => {
    if (!showResults) {
      return {
        border: '2px solid #e9ecef',
        borderRadius: '12px',
        backgroundColor: 'white',
        color: 'black',
      };
    }

    const questionResult = getQuestionResult(questionId);
    if (!questionResult) {
      return {
        border: '2px solid #e9ecef',
        borderRadius: '12px',
        backgroundColor: 'white',
        color: 'black',
      };
    }

    const userAnswer = userAnswers[questionId];
    const userSelected = userAnswer && userAnswer.choiceText === choiceText;
    const isCorrectChoice = questionResult.correctChoiceText === choiceText;

    // Show correct answer in green
    if (userSelected && isCorrectChoice) {
      return {
        border: '2px solid #1D8B2A',
        borderRadius: '12px',
        backgroundColor: '#1D8B2A',
        color: 'white',
      };
    }

    // Show user's wrong answer in red
    if (userSelected && !questionResult.isCorrect) {
      return {
        border: '2px solid #D40307',
        borderRadius: '12px',
        backgroundColor: '#D40307',
        color: 'white',
      };
    }

    // Default style for other choices
    return {
      border: '2px solid #e9ecef',
      borderRadius: '12px',
      backgroundColor: 'white',
      color: 'black',
    };
  };

  const handleAnswerChange = (questionId, choiceText, choiceIndex) => {
    setUserAnswers((prev) => ({
      ...prev,
      [questionId]: { choiceText, choiceIndex },
    }));
  };

  const handleShowAnswers = () => {
    // Add a simple check to see if function is called
    document.title = 'Marking answers...';

    if (!resComp.payload) {
      return;
    }

    if (Object.keys(userAnswers).length === 0) {
      return;
    }

    // Clear all manual styles applied by onChange handlers
    const allLabels = document.querySelectorAll('label[for*="q"]');
    allLabels.forEach((label) => {
      label.style.backgroundColor = '';
      label.style.borderColor = '';
      label.style.color = '';
    });

    // Check if payload is direct competition or wrapped in rows
    const competition = resComp.payload;

    // Debug: Log the data we have
    document.title = `Competition has ${
      competition?.questionSets?.length || 0
    } sets, ${Object.keys(userAnswers).length} answers`;

    const answers = Object.entries(userAnswers).map(
      ([questionId, answerData]) => {
        // Now we have both the choice text and the original choice index
        const { choiceText, choiceIndex } = answerData;

        return {
          questionId,
          answered: choiceIndex,
          selectedChoiceText: choiceText,
        };
      },
    );
    // Debug: Show what we're sending
    document.title = `Sending ${answers.length} answers`;

    reqMarkings({
      url: 'api/quiz/competitions/mock/mark',
      method: 'POST',
      data: { answers },
    });

    setShowResults(true);
  };

  useEffect(() => {
    loadNewQuestions();

    // Hide floating mock button when on mock page
    const floatingButton = document.querySelector('.floating-mock-button');
    if (floatingButton) {
      floatingButton.style.display = 'none';
    }

    // Show floating button again when component unmounts
    return () => {
      const cleanupFloatingButton = document.querySelector(
        '.floating-mock-button',
      );
      if (cleanupFloatingButton) {
        cleanupFloatingButton.style.display = 'block';
      }
    };
  }, []);

  useEffect(() => {
    if (showResults) {
      setTimeout(() => {
        if (scoreRef.current) {
          scoreRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }, 100);
    }
  }, [showResults]);
  return (
    <div style={{ backgroundColor: '#EFF2F5', minHeight: '100vh' }}>
      <div className="container pt-1">
        {/* Mock Banner */}

        <div className="row justify-content-center">
          <div className="col-12 col-md-10 col-lg-8">
            <div className="ratio-16-4">
              <img
                src="assets/img/mock-banner.svg"
                alt="Mock Competition Banner"
                className="img-fluid w-100 h-100"
                style={{ objectFit: 'contain' }}
              />
            </div>
          </div>
          <div className="col-12 col-md-10 col-lg-8">
            <div className="card shadow-sm">
              <div className="card-body p-4">
                <PageHttp res={resComp}>
                  {() => {
                    if (resComp.payload) {
                      const competition = resComp.payload;
                      return (
                        <div>
                          {/* Score Display */}
                          {showResults && resMarkings.payload && (
                            <div
                              ref={scoreRef}
                              className="alert alert-info text-center mb-4"
                              style={{
                                backgroundColor: '#FFF6E3',
                                borderRadius: '12px',
                                border: '1px solid #FFECB3',
                              }}
                            >
                              <div className="d-flex align-items-center justify-content-center">
                                <img
                                  src="assets/img/mock-icon.svg"
                                  alt="Mock Icon"
                                  style={{
                                    width: '66px',
                                    height: '66px',
                                    marginRight: '12px',
                                  }}
                                />
                                <span
                                  style={{
                                    color: 'black',
                                    fontSize: '18px',
                                    fontWeight: 500,
                                  }}
                                >
                                  你獲得了{' '}
                                  <span style={{ color: '#1C64D0' }}>
                                    {resMarkings.payload.score || 0} /{' '}
                                    {(resMarkings.payload.totalQuestions || 0)
                                      * 10}
                                  </span>{' '}
                                  分。請繼續努力!
                                </span>
                              </div>
                            </div>
                          )}

                          {competition.questionSets
                            && competition.questionSets.map((set, index) => (
                              <div key={index} className="mb-4">
                                {set.questions
                                  && set.questions.map((question, qIndex) => (
                                    <div key={qIndex} className="mb-4">
                                      {/* Question Number Badge */}
                                      <div className="d-flex align-items-start mb-3" style={{ gap: '8px' }}>
                                        <div className="me-3 position-relative d-flex">
                                          <img
                                            src="assets/img/mock-badge.svg"
                                            alt="Question Badge"
                                            style={{
                                              width: '40px',
                                              height: '40px',
                                            }}
                                          />
                                          <div
                                            className="position-absolute"
                                            style={{
                                              fontSize: '16px',
                                              fontWeight: '700',
                                              color: 'white',
                                              textAlign: 'center',
                                              top: '50%',
                                              left: '50%',
                                              transform:
                                                'translate(-50%, -50%)',
                                            }}
                                          >
                                            {qIndex + 1}
                                          </div>
                                        </div>
                                        <Html>{question.question}</Html>
                                      </div>

                                      {question.img && (
                                        <div className="mb-3 text-center">
                                          <img
                                            src={question.img}
                                            alt="Question"
                                            className="img-fluid"
                                            style={{
                                              maxHeight: '300px',
                                              borderRadius: '8px',
                                            }}
                                          />
                                        </div>
                                      )}

                                      {question.choices && (
                                        <div className="row">
                                          {question.choices.map(
                                            (choice, cIndex) => (
                                              <div
                                                key={cIndex}
                                                className="col-md-6 mb-3"
                                              >
                                                <label
                                                  htmlFor={`q${index}_${qIndex}_${cIndex}`}
                                                  className="d-flex align-items-center w-100 position-relative"
                                                  style={{
                                                    ...getChoiceStyle(
                                                      question._id,
                                                      cIndex,
                                                      choice,
                                                    ),
                                                    cursor: showResults
                                                      ? 'default'
                                                      : 'pointer',
                                                    transition: 'all 0.2s',
                                                    margin: 0,
                                                    padding: '16px 24px',
                                                  }}
                                                  onMouseEnter={(e) => {
                                                    if (!showResults) {
                                                      const input = e.currentTarget.querySelector(
                                                        'input',
                                                      );
                                                      if (!input.checked) {
                                                        e.currentTarget.style.borderColor = '#007bff';
                                                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                                                      }
                                                    }
                                                  }}
                                                  onMouseLeave={(e) => {
                                                    if (!showResults) {
                                                      const input = e.currentTarget.querySelector(
                                                        'input',
                                                      );
                                                      if (!input.checked) {
                                                        e.currentTarget.style.borderColor = '#e9ecef';
                                                        e.currentTarget.style.backgroundColor = 'white';
                                                      }
                                                    }
                                                  }}
                                                >
                                                  <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    name={`q${index}_${qIndex}`}
                                                    id={`q${index}_${qIndex}_${cIndex}`}
                                                    checked={
                                                      userAnswers[
                                                        question._id
                                                      ]
                                                      && userAnswers[question._id]
                                                        .choiceIndex === cIndex
                                                    }
                                                    style={{
                                                      marginTop: 0,
                                                      marginRight: '12px',
                                                      flexShrink: 0,
                                                      position: 'static',
                                                      marginLeft: 0,
                                                      accentColor: (() => {
                                                        if (!showResults) return '#1C64D0';

                                                        const result = getQuestionResult(
                                                          question._id,
                                                        );
                                                        if (!result) return '#1C64D0';

                                                        const userAnswer = userAnswers[
                                                          question._id
                                                        ];
                                                        const userSelected = userAnswer
                                                          && userAnswer.choiceText
                                                            === choice;
                                                        const isCorrectChoice = result.correctChoiceText
                                                          === choice;

                                                        if (isCorrectChoice) {
                                                          return '#1D8B2A'; // Green for correct answer
                                                        }

                                                        if (
                                                          userSelected
                                                          && !result.isCorrect
                                                        ) {
                                                          return '#D40307'; // Red for wrong answer
                                                        }

                                                        return '#1C64D0'; // Default blue
                                                      })(),
                                                      width: '20px',
                                                      height: '20px',
                                                    }}
                                                    onChange={(e) => {
                                                      if (!showResults) {
                                                        // Track user's answer
                                                        handleAnswerChange(
                                                          question._id,
                                                          choice,
                                                          cIndex,
                                                        );

                                                        // Reset all labels for this question
                                                        const labels = document.querySelectorAll(
                                                          `[for*="q${index}_${qIndex}"]`,
                                                        );
                                                        labels.forEach(
                                                          (label) => {
                                                            label.style.backgroundColor = 'white';
                                                            label.style.borderColor = '#e9ecef';
                                                            label.style.color = 'black';
                                                          },
                                                        );

                                                        // Style the selected label
                                                        if (e.target.checked) {
                                                          const selectedLabel = e.target.closest(
                                                            'label',
                                                          );
                                                          selectedLabel.style.backgroundColor = '#1C64D0';
                                                          selectedLabel.style.borderColor = '#1C64D0';
                                                          selectedLabel.style.color = 'white';
                                                        }
                                                      }
                                                    }}
                                                  />
                                                  <div
                                                    style={{
                                                      fontSize: '15px',
                                                      lineHeight: '1.4',
                                                      flex: 1,
                                                    }}
                                                  >
                                                    <strong>
                                                      {String.fromCharCode(
                                                        65 + cIndex,
                                                      )}
                                                      .
                                                    </strong>{' '}
                                                    {choice}
                                                  </div>
                                                  {showResults
                                                    && (() => {
                                                      const result = getQuestionResult(
                                                        question._id,
                                                      );
                                                      if (!result) return null;

                                                      const userAnswer = userAnswers[
                                                        question._id
                                                      ];
                                                      const userSelected = userAnswer
                                                        && userAnswer.choiceText
                                                          === choice;
                                                      const isCorrectChoice = result.correctChoiceText
                                                        === choice;

                                                      if (isCorrectChoice) {
                                                        return (
                                                          <div
                                                            style={{
                                                              fontSize: '20px',
                                                              color: 'white',
                                                            }}
                                                          >
                                                            ✓
                                                          </div>
                                                        );
                                                      }

                                                      if (
                                                        userSelected
                                                        && !result.isCorrect
                                                      ) {
                                                        return (
                                                          <div
                                                            style={{
                                                              fontSize: '20px',
                                                              color: 'white',
                                                            }}
                                                          >
                                                            ✕
                                                          </div>
                                                        );
                                                      }

                                                      return null;
                                                    })()}
                                                </label>
                                              </div>
                                            ),
                                          )}
                                        </div>
                                      )}

                                      {/* Show correct answer only for wrong answers */}
                                      {showResults
                                        && (() => {
                                          const result = getQuestionResult(
                                            question._id,
                                          );
                                          if (!result || result.isCorrect) return null;

                                          return (
                                            <div
                                              className="mt-3 p-3"
                                              style={{
                                                backgroundColor: '#FFF6E3',
                                                borderRadius: '5px',
                                                border: '1px solid #FFF6E3',
                                                fontSize: '16px',
                                              }}
                                            >
                                              <strong
                                                style={{ color: 'black' }}
                                              >
                                                正確答案:{' '}
                                              </strong>
                                              <span>
                                                {result.correctChoiceText}
                                              </span>
                                            </div>
                                          );
                                        })()}
                                    </div>
                                  ))}
                              </div>
                            ))}

                          <div
                            className="text-center mt-5 pt-4"
                            style={{ borderTop: '1px solid #dee2e6' }}
                          >
                            <button
                              onClick={() => {
                                if (showResults) {
                                  // Try again - reset everything and load new questions
                                  loadNewQuestions();
                                  if (scoreRef.current) {
                                    scoreRef.current.scrollIntoView({
                                      behavior: 'smooth',
                                      block: 'center',
                                    });
                                  }
                                } else {
                                  // Show answers - mark current answers
                                  handleShowAnswers();
                                }
                              }}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 0,
                              }}
                            >
                              <img
                                src={
                                  showResults
                                    ? 'assets/img/mock-tryAgain.svg'
                                    : 'assets/img/mock-showAns.svg'
                                }
                                alt={showResults ? 'Try Again' : 'Show Answers'}
                                style={{
                                  maxWidth: '200px',
                                  height: 'auto',
                                }}
                              />
                            </button>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div className="alert alert-warning text-center">
                        <h5>未有可用的模擬挑戰</h5>
                        <p className="mb-0">請稍後再試或聯絡管理員</p>
                      </div>
                    );
                  }}
                </PageHttp>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageMock;
